// src/components/DataTable.tsx
/**
 * DataTable (generic)
 * -----------------------------------------------------------------------------
 * Lightweight, typed table with:
 * - Column chooser (persisted to localStorage via `storageKey`)
 * - CSV export (uses `filename` or "export")
 * - Sticky header / zebra rows via utility classes on the container
 *
 * Design:
 * - Expects parent to wrap with `.table-shell table-sticky table-zebra` classes.
 * - Numeric alignment: set `align: 'right'` and/or provide `.tabular-nums` in `className`.
 *
 * Compatibility:
 * - Accepts `label` OR `header` for column title (both supported).
 * - Exports a generic `Col<T = any>` type so callsites may use `Col<Job>[]`
 *   or plain `Col[]`. The component itself is also generic: `<DataTable<Job> />`.
 */

import React, { useEffect, useMemo, useState } from "react";
import { get, set } from "@/lib/storage";
import { Download, Settings2 } from "lucide-react";

export type CellRenderer<T = any> = (row: T, rowIndex: number) => React.ReactNode;

export type Col<T = any> = {
  /** Unique key for this column. Also used to read raw values if no accessor/render provided. */
  key: string;
  /** Title for the column. If `label` missing, `header` is used (legacy compatibility). */
  label?: string;
  /** Legacy alias for label (accepted). */
  header?: string;
  /** Optional width in pixels. */
  width?: number;
  /** Optional horizontal alignment. */
  align?: "left" | "right" | "center";
  /** Optional CSS classes applied to cells for this column. */
  className?: string;
  /** Render function for a cell. */
  render?: CellRenderer<T>;
  /** Accessor for export & default cell text when no render provided. */
  accessor?: (row: T) => string | number | null | undefined;
};

type TablePrefs = {
  visible: string[];
};

type Props<T = any> = {
  columns: Col<T>[];
  rows: T[];
  /** CSV filename (without extension). */
  filename?: string;
  /** localStorage key for column visibility prefs. */
  storageKey?: string;
  /** Optional aria-label for the table */
  ariaLabel?: string;
};

function useColumnPrefs(allKeys: string[], storageKey?: string) {
  const [visible, setVisible] = useState<string[]>(() => {
    if (!storageKey) return allKeys;
    const saved = get<TablePrefs>(storageKey, { visible: allKeys });
    if (!saved || !Array.isArray(saved.visible) || saved.visible.length === 0) {
      return allKeys;
    }
    // Ensure new/removed columns reconcile with saved prefs
    const setAll = new Set(allKeys);
    return saved.visible.filter((k) => setAll.has(k));
  });

  useEffect(() => {
    if (!storageKey) return;
    set<TablePrefs>(storageKey, { visible });
  }, [visible, storageKey]);

  // If columns change, ensure visibility includes any new ones
  useEffect(() => {
    setVisible((prev) => {
      const s = new Set(prev);
      let changed = false;
      for (const k of allKeys) {
        if (!s.has(k)) {
          s.add(k);
          changed = true;
        }
      }
      return changed ? Array.from(s) : prev;
    });
  }, [allKeys.join("|")]);

  return { visible, setVisible };
}

function toPlain(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (val instanceof Date) return val.toISOString();
  // For objects/arrays, fall back to JSON (safe for CSV)
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

function exportCSV<T>(rows: T[], cols: Col<T>[], filename = "export") {
  const headers = cols.map((c) => toPlain(c.label ?? c.header ?? c.key));
  const lines = [headers.join(",")];

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const line = cols.map((c) => {
      let raw: any;
      if (typeof c.accessor === "function") raw = c.accessor(r);
      else if (c.render) {
        // Rendered nodes are not ideal for CSV; try key lookup as fallback
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        raw = (r as any)[c.key];
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        raw = (r as any)[c.key];
      }
      const text = toPlain(raw);
      // Escape CSV fields containing comma/quote/newline
      if (/[,"\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    });
    lines.push(line.join(","));
  }

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function DataTable<T = any>({
  columns,
  rows,
  filename = "export",
  storageKey,
  ariaLabel,
}: Props<T>) {
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const { visible, setVisible } = useColumnPrefs(allKeys, storageKey);
  const [chooserOpen, setChooserOpen] = useState(false);

  const activeCols = useMemo(
    () => columns.filter((c) => visible.includes(c.key)),
    [columns, visible]
  );

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 mb-2">
        <button
          className="btn btn-secondary"
          onClick={() => exportCSV(rows, activeCols, filename)}
          title="Export CSV"
        >
          <Download className="w-4 h-4 mr-2" />
          Export
        </button>
        <div className="relative">
          <button
            className="btn btn-secondary"
            onClick={() => setChooserOpen((v) => !v)}
            title="Columns"
            aria-expanded={chooserOpen}
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Columns
          </button>
          {chooserOpen && (
            <div className="absolute right-0 top-full mt-2 z-40 w-64 bg-white border rounded shadow-lg p-2">
              <div className="text-sm font-medium mb-2">Visible Columns</div>
              <div className="max-h-64 overflow-auto pr-1">
                {columns.map((c) => {
                  const id = `col_${c.key}`;
                  return (
                    <label
                      key={c.key}
                      htmlFor={id}
                      className="flex items-center gap-2 py-1 text-sm cursor-pointer"
                    >
                      <input
                        id={id}
                        type="checkbox"
                        className="accent-[var(--brand-600)]"
                        checked={visible.includes(c.key)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setVisible((prev) => {
                            const setPrev = new Set(prev);
                            if (checked) setPrev.add(c.key);
                            else setPrev.delete(c.key);
                            return Array.from(setPrev);
                          });
                        }}
                      />
                      <span className="truncate">
                        {c.label ?? c.header ?? c.key}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto">
        <table className="w-full text-sm" aria-label={ariaLabel}>
          <thead className="bg-white">
            <tr>
              {activeCols.map((c) => (
                <th
                  key={c.key}
                  className={`text-left font-medium border-b px-3 py-2 sticky top-0 bg-white ${
                    c.align === "right"
                      ? "text-right"
                      : c.align === "center"
                      ? "text-center"
                      : "text-left"
                  }`}
                  style={c.width ? { width: c.width } : undefined}
                  scope="col"
                >
                  {c.label ?? c.header ?? c.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-4 text-center text-slate-500"
                  colSpan={activeCols.length || 1}
                >
                  No rows
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr key={ri} className="border-b last:border-b-0">
                  {activeCols.map((c) => {
                    const classAlign =
                      c.align === "right"
                        ? "text-right"
                        : c.align === "center"
                        ? "text-center"
                        : "text-left";
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const fallback = (row as any)[c.key];
                    const content =
                      c.render?.(row, ri) ??
                      (typeof c.accessor === "function"
                        ? c.accessor(row)
                        : fallback ?? "");
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 align-top ${classAlign} ${
                          c.className || ""
                        }`}
                        style={c.width ? { width: c.width } : undefined}
                      >
                        {content as React.ReactNode}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
